import React, { useState, useEffect, useCallback } from 'react';
import { Bell, ExternalLink, X, Archive, FileText, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const NotificationCarousel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    if (!user) return; // Don't fetch if not logged in
    
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/notifications/carousel`);
      // Sort by date descending (newest first)
      const sorted = (response.data.notifications || []).sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setNotifications(sorted);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 60000);
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]);

  const getCategoryColor = (category) => {
    switch (category) {
      case 'urgent': return 'bg-red-500';
      case 'annulation': return 'bg-orange-500';
      case 'facturation': return 'bg-amber-500';
      case 'soins': return 'bg-emerald-500';
      case 'cours': return 'bg-blue-500';
      case 'vie_club': return 'bg-purple-500';
      case 'groupe': return 'bg-indigo-500';
      case 'tarifs': return 'bg-teal-500';
      case 'cheval': return 'bg-cyan-500';
      default: return 'bg-stone-500';
    }
  };

  const getCategoryBgColor = (category, isRead) => {
    if (isRead) return 'bg-stone-100 border-stone-300';
    switch (category) {
      case 'urgent': return 'bg-red-100 border-red-400 shadow-sm shadow-red-100';
      case 'annulation': return 'bg-orange-100 border-orange-400 shadow-sm shadow-orange-100';
      case 'facturation': return 'bg-amber-100 border-amber-400 shadow-sm shadow-amber-100';
      case 'soins': return 'bg-emerald-100 border-emerald-400 shadow-sm shadow-emerald-100';
      case 'cours': return 'bg-blue-100 border-blue-400 shadow-sm shadow-blue-100';
      case 'vie_club': return 'bg-purple-100 border-purple-400 shadow-sm shadow-purple-100';
      case 'groupe': return 'bg-indigo-100 border-indigo-400 shadow-sm shadow-indigo-100';
      case 'tarifs': return 'bg-teal-100 border-teal-400 shadow-sm shadow-teal-100';
      case 'cheval': return 'bg-cyan-100 border-cyan-400 shadow-sm shadow-cyan-100';
      default: return 'bg-violet-100 border-violet-400 shadow-sm shadow-violet-100';
    }
  };

  // Map notification type to category for coloring
  const getNotificationCategory = (notif) => {
    const type = notif.type || notif.notification_type || '';
    const category = notif.category;
    
    // If category is already set, use it
    if (category) return category;
    
    // Otherwise, derive from type
    if (type.includes('suppression') || type.includes('annul')) return 'annulation';
    if (type.includes('facture') || type.includes('avoir') || type.includes('paiement')) return 'facturation';
    if (type.includes('bareme') || type.includes('tarif')) return 'tarifs';
    if (type.includes('cours') || type.includes('stage') || type.includes('inscription')) return 'cours';
    if (type.includes('soins') || type.includes('sante')) return 'soins';
    if (type.includes('cheval') || type.includes('commentaire')) return 'cheval';
    if (type.includes('groupe')) return 'groupe';
    
    return 'vie_club';
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'urgent': return 'Urgent';
      case 'annulation': return 'Annulation';
      case 'facturation': return 'Facturation';
      case 'soins': return 'Soins';
      case 'cours': return 'Cours';
      case 'vie_club': return 'Vie du club';
      default: return 'Information';
    }
  };

  const handleNotificationClick = async (notif) => {
    // Open modal with notification details
    setSelectedNotification(notif);
    setIsModalOpen(true);
    
    // Mark as read if not already (explicitly set archive=false to not archive)
    if (!notif.lu) {
      try {
        await axios.put(`${API_URL}/api/notifications/${notif.id}/read?archive=false`);
        // Update local state
        setNotifications(prev => 
          prev.map(n => n.id === notif.id ? { ...n, lu: true } : n)
        );
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
  };

  const handleArchive = async () => {
    if (!selectedNotification) return;
    
    try {
      await axios.put(`${API_URL}/api/notifications/${selectedNotification.id}/read?archive=true`);
      toast.success('Notification archivée');
      // Remove from list
      setNotifications(prev => prev.filter(n => n.id !== selectedNotification.id));
      setIsModalOpen(false);
      setSelectedNotification(null);
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedNotification(null);
  };

  const handleLinkClick = (link) => {
    if (link.type === 'external') {
      window.open(link.url, '_blank');
    } else {
      handleCloseModal();
      navigate(link.url);
    }
  };

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: fr });
    } catch {
      return '';
    }
  };

  const formatFullDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd MMMM yyyy à HH:mm', { locale: fr });
    } catch {
      return '';
    }
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const unreadCount = notifications.filter(n => !n.lu).length;

  if (loading) {
    return (
      <Card className="bg-stone-50/50" data-testid="notification-carousel">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#2E4A3D]" />
            Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="text-center py-4 text-stone-500">
            <div className="animate-spin w-6 h-6 border-2 border-stone-300 border-t-[#2E4A3D] rounded-full mx-auto mb-2" />
            <p className="text-sm">Chargement...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (notifications.length === 0) {
    return (
      <Card className="bg-stone-50/50" data-testid="notification-carousel">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#2E4A3D]" />
            Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="text-center py-4 text-stone-500">
            <Bell className="w-6 h-6 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Aucune notification</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-stone-50/50" data-testid="notification-carousel">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#2E4A3D]" />
              Notifications
              {unreadCount > 0 && (
                <span className="bg-[#E07A5F] text-white text-xs px-2 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </CardTitle>
            <button
              onClick={() => navigate('/notifications')}
              className="text-xs text-[#2E4A3D] hover:underline"
            >
              Voir tout
            </button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Vertical scrolling list - max height with scroll */}
          <div className="max-h-[300px] overflow-y-auto space-y-2 pr-1">
            {notifications.map((notif) => {
              const category = getNotificationCategory(notif);
              const bgColors = {
                urgent: '#FEE2E2',      // red-100
                annulation: '#FFEDD5',   // orange-100
                facturation: '#FEF3C7', // amber-100
                soins: '#D1FAE5',        // emerald-100
                cours: '#DBEAFE',        // blue-100
                vie_club: '#EDE9FE',     // purple-100
                groupe: '#E0E7FF',       // indigo-100
                tarifs: '#CCFBF1',       // teal-100
                cheval: '#CFFAFE',       // cyan-100
                default: '#F3E8FF'       // violet-100
              };
              const borderColors = {
                urgent: '#FCA5A5',      // red-300
                annulation: '#FDBA74',   // orange-300
                facturation: '#FCD34D', // amber-300
                soins: '#6EE7B7',        // emerald-300
                cours: '#93C5FD',        // blue-300
                vie_club: '#C4B5FD',     // purple-300
                groupe: '#A5B4FC',       // indigo-300
                tarifs: '#5EEAD4',       // teal-300
                cheval: '#67E8F9',       // cyan-300
                default: '#D8B4FE'       // violet-300
              };
              const bgColor = notif.lu ? '#F5F5F4' : (bgColors[category] || bgColors.default);
              const borderColor = notif.lu ? '#D6D3D1' : (borderColors[category] || borderColors.default);
              
              return (
              <div
                key={notif.id}
                onClick={() => handleNotificationClick(notif)}
                style={{ 
                  backgroundColor: bgColor,
                  borderColor: borderColor,
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }}
                className={`flex items-center px-3 py-2.5 rounded-lg cursor-pointer transition-all ${
                  notif.lu ? 'opacity-60' : 'hover:shadow-md'
                }`}
                data-testid={`notification-item-${notif.id}`}
              >
                {/* Category indicator */}
                <div className={`w-1.5 h-10 rounded-full mr-3 flex-shrink-0 ${getCategoryColor(category)}`} />
                
                <div className="flex-1 min-w-0">
                  {/* Line 1: Title + time */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {!notif.lu && (
                        <span className="w-2 h-2 rounded-full bg-[#E07A5F] flex-shrink-0" />
                      )}
                      <p className={`font-medium text-sm truncate ${
                        notif.lu ? 'text-stone-500' : 'text-stone-900'
                      }`}>
                        {notif.titre}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {notif.links && notif.links.length > 0 && (
                        <ExternalLink className="w-3 h-3 text-stone-400" />
                      )}
                      <span className={`text-[10px] whitespace-nowrap ${
                        notif.lu ? 'text-stone-400' : 'text-stone-500'
                      }`}>
                        {formatTime(notif.created_at)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Line 2: Content (truncated) */}
                  <p className={`text-xs truncate ${
                    notif.lu ? 'text-stone-400' : 'text-stone-500'
                  }`}>
                    {stripHtml(notif.contenu || notif.message || '')}
                  </p>
                </div>
              </div>
            );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Notification Detail Modal */}
      {isModalOpen && selectedNotification && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center pt-4 sm:pt-0 overflow-y-auto">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50" 
            onClick={handleCloseModal}
          />
          
          {/* Modal - positioned higher on mobile */}
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 my-4 sm:my-0 max-h-[85vh] sm:max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-stone-200">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${getCategoryColor(selectedNotification.category)}`} />
                <span className="text-sm font-medium text-stone-600">
                  {getCategoryLabel(selectedNotification.category)}
                </span>
              </div>
              <button
                onClick={handleCloseModal}
                className="p-1 hover:bg-stone-100 rounded-full transition-colors"
                data-testid="close-notification-modal"
              >
                <X className="w-5 h-5 text-stone-500" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <h2 className="text-xl font-semibold text-stone-900 mb-2">
                {selectedNotification.titre}
              </h2>
              
              <p className="text-xs text-stone-500 mb-4">
                {selectedNotification.created_by_name && (
                  <span>Par {selectedNotification.created_by_name} • </span>
                )}
                {formatFullDate(selectedNotification.created_at)}
              </p>
              
              <div 
                className="prose prose-sm max-w-none text-stone-700"
                dangerouslySetInnerHTML={{ __html: selectedNotification.contenu || selectedNotification.message || '' }}
              />
              
              {/* Attachment */}
              {selectedNotification.attachment_path && (
                <div className="mt-4 p-3 bg-stone-50 rounded-lg border border-stone-200">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-stone-500" />
                    <span className="text-sm text-stone-700 flex-1">
                      {selectedNotification.attachment_name || 'Pièce jointe'}
                    </span>
                    <a
                      href={`${API_URL}${selectedNotification.attachment_path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#2E4A3D] hover:underline text-sm flex items-center gap-1"
                    >
                      <Download className="w-4 h-4" />
                      Télécharger
                    </a>
                  </div>
                </div>
              )}
              
              {/* Links */}
              {selectedNotification.links && selectedNotification.links.length > 0 && (
                <div className="mt-4 space-y-2">
                  {selectedNotification.links.map((link, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleLinkClick(link)}
                      className="flex items-center gap-2 text-[#2E4A3D] hover:underline text-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      {link.label || link.url}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Footer with Archive button */}
            <div className="flex items-center justify-end gap-2 p-4 border-t border-stone-200 bg-stone-50">
              <Button
                variant="outline"
                size="sm"
                onClick={handleArchive}
                className="text-stone-600 hover:bg-stone-100"
                data-testid="archive-notification-btn"
              >
                <Archive className="w-4 h-4 mr-2" />
                Archiver
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleCloseModal}
                className="bg-[#2E4A3D] hover:bg-[#243D32]"
              >
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationCarousel;
